import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, UserX } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ConfirmModal from "../ConfirmModal";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import { employeeName, invalidateEmployees } from "./WhtPickers";

// The client's employees for the compensation rows (F7a): list, add, edit,
// deactivate (DELETE deletes an employee with no rows, otherwise deactivates).

const inputClass = "w-full border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500";
const EMPTY = { lastName: "", firstName: "", middleName: "", tin: "", isMwe: false };

const EmployeeForm = ({ initial, onSave, onCancel, errors }) => {
  const [form, setForm] = useState(initial);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const { mutate: save, isPending } = useGuardedMutation(() => onSave(form));
  const err = (k) => errors?.[k] && <p role="alert" className="text-xs text-danger-500 mt-0.5">{errors[k]}</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-start border border-ink-line rounded-md p-3 bg-ink-canvas" data-testid="employee-form">
      <label className="text-xs font-medium text-ink-body md:col-span-1">Last name
        <input aria-label="Last name" value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} className={`${inputClass} mt-1`} />
        {err("lastName")}
      </label>
      <label className="text-xs font-medium text-ink-body md:col-span-1">First name
        <input aria-label="First name" value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} className={`${inputClass} mt-1`} />
        {err("firstName")}
      </label>
      <label className="text-xs font-medium text-ink-body md:col-span-1">Middle name
        <input aria-label="Middle name" value={form.middleName} onChange={(e) => set({ middleName: e.target.value })} className={`${inputClass} mt-1`} />
      </label>
      <label className="text-xs font-medium text-ink-body md:col-span-1">TIN
        <input aria-label="TIN" value={form.tin} onChange={(e) => set({ tin: e.target.value })} placeholder="123-456-789-000" className={`${inputClass} mt-1`} />
        {err("tin")}
      </label>
      <label className="flex items-center gap-2 text-sm text-ink md:col-span-1 mt-6">
        <input type="checkbox" checked={form.isMwe} onChange={(e) => set({ isMwe: e.target.checked })} className="h-4 w-4 accent-primary-500" />
        Minimum wage earner
      </label>
      <div className="flex gap-2 md:col-span-1 mt-5 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>Cancel</Button>
        <Button size="sm" onClick={() => save()} loading={isPending}>Save</Button>
      </div>
    </div>
  );
};

const EmployeesPanel = ({ client, onChanged }) => {
  const clientId = client?._id;
  const [employees, setEmployees] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // 'new' | employee
  const [errors, setErrors] = useState({});
  const [pendingDeactivate, setPendingDeactivate] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      setEmployees((await withholdingTaxAPI.listEmployees(clientId, { includeInactive: showInactive })).data || []);
    } catch (e) {
      notify.error("Couldn't load employees", e.message);
    }
  }, [clientId, showInactive]);
  useEffect(() => { load(); }, [load]);

  const changed = async () => {
    invalidateEmployees(clientId);
    await load();
    onChanged?.();
  };

  const save = async (form) => {
    setErrors({});
    const body = {
      lastName: form.lastName.trim(), firstName: form.firstName.trim(), middleName: form.middleName.trim(),
      tin: form.tin.trim(), isMwe: form.isMwe,
    };
    try {
      if (editing === "new") await withholdingTaxAPI.createEmployee(clientId, body);
      else await withholdingTaxAPI.updateEmployee(clientId, editing._id, body);
      notify.success("Saved", `${employeeName(body)} saved.`);
      setEditing(null);
      await changed();
    } catch (e) {
      setErrors(e.code === "DUPLICATE_EMPLOYEE_TIN" ? { tin: e.message } : (e.lineErrors?.[0]?.fields || {}));
      notify.error("Couldn't save", e.message);
    }
  };

  const deactivate = async () => {
    try {
      const res = await withholdingTaxAPI.deactivateEmployee(clientId, pendingDeactivate._id);
      notify.success(res.data?.deactivated ? "Deactivated" : "Deleted",
        res.data?.deactivated ? "The employee has compensation rows, so they were deactivated, not deleted." : "The employee was deleted.");
      setPendingDeactivate(null);
      await changed();
    } catch (e) {
      notify.error("Couldn't deactivate", e.message);
    }
  };
  const { mutate: runDeactivate, isPending: deactivating } = useGuardedMutation(deactivate);

  return (
    <section aria-labelledby="employees-heading" className="border border-ink-line rounded-lg bg-white p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 id="employees-heading" className="text-base font-medium text-ink">Employees</h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-ink-body">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-4 w-4 accent-primary-500" />
            Show deactivated
          </label>
          <Button variant="outline" size="sm" icon={Plus} onClick={() => { setErrors({}); setEditing("new"); }}>Add employee</Button>
        </div>
      </div>

      {editing === "new" && <EmployeeForm initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} errors={errors} />}

      <table className="min-w-full text-sm mt-2">
        <thead>
          <tr className="text-xs font-medium text-ink-body uppercase tracking-wider">
            <th className="text-left py-1">Name</th>
            <th className="text-left py-1">TIN</th>
            <th className="text-left py-1">MWE</th>
            <th className="text-left py-1">Status</th>
            <th className="py-1"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {employees.length === 0 && (
            <tr><td colSpan={5} className="py-3 text-center text-ink-body">No employees yet.</td></tr>
          )}
          {employees.map((e) => (editing && editing !== "new" && editing._id === e._id ? (
            <tr key={e._id}><td colSpan={5} className="py-2">
              <EmployeeForm
                initial={{ lastName: e.lastName, firstName: e.firstName, middleName: e.middleName || "", tin: e.tin ? `${e.tin}${e.tinBranch || ""}` : "", isMwe: Boolean(e.isMwe) }}
                onSave={save}
                onCancel={() => setEditing(null)}
                errors={errors}
              />
            </td></tr>
          ) : (
            <tr key={e._id} className="border-t border-ink-line" data-employee-id={e._id}>
              <td className="py-2 text-ink">{employeeName(e)}</td>
              <td className="py-2 text-ink tabular-nums">{e.tin ? `${e.tin}${e.tinBranch ? `-${e.tinBranch}` : ""}` : <span className="text-warning-700">no TIN</span>}</td>
              <td className="py-2">{e.isMwe && <span className="text-xs px-2 py-0.5 rounded-md bg-primary-50 text-primary-700">MWE</span>}</td>
              <td className="py-2 text-ink-body">{e.isActive === false ? "Deactivated" : "Active"}</td>
              <td className="py-2 text-right">
                <Button variant="ghost" size="sm" icon={Pencil} onClick={() => { setErrors({}); setEditing(e); }} aria-label={`Edit ${employeeName(e)}`} />
                {e.isActive !== false && (
                  <Button variant="dangerGhost" size="sm" icon={UserX} onClick={() => setPendingDeactivate(e)} aria-label={`Deactivate ${employeeName(e)}`} />
                )}
              </td>
            </tr>
          )))}
        </tbody>
      </table>

      <ConfirmModal
        isOpen={Boolean(pendingDeactivate)}
        title="Deactivate this employee?"
        message={pendingDeactivate ? `${employeeName(pendingDeactivate)} will no longer be offered on new rows. An employee with no compensation rows is deleted instead.` : ""}
        confirmLabel="Deactivate"
        loading={deactivating}
        onConfirm={() => runDeactivate()}
        onCancel={() => setPendingDeactivate(null)}
      />
    </section>
  );
};

export default EmployeesPanel;
